import React, { useCallback, useState } from 'react'

import cls from 'classnames'
import { useSelector } from 'react-redux'
import { Link } from 'react-static'
import SbEditable from 'storyblok-react'

import './NavBar.css'
import { serverURL } from './config'
import psLogo from './images/ps-logo.png'

export default function NavBar({
  headerStory,
  lng,
  handleLanguageChange,
  loggingOut,
  logOut,
}) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [activeDropdownIndex, setActiveDropdownIndex] = useState(null)

  const user = useSelector(state => state.user)

  const header = headerStory.content.Links

  const getDesktopLinks = useCallback(
    el => {
      const httpRegex = /^http/

      if (el.component == 'Text' && user && user.role == 'admin') {
        return (
          <a
            target='_blank'
            key={`je-${el._uid}`}
            href={`${serverURL}/users`}
            className='block w-full bg-green-500 px-6 py-4 text-center text-sm text-white no-underline transition-colors duration-300 hover:bg-green-800'
            rel='noreferrer'
          >
            {el.text}
          </a>
        )
      }

      if (el.component === 'Footer Links') {
        const title = el.content.find(
          item => item.component === 'Section Title',
        )
        const links = el.content.filter(item => item.component === 'Link')

        return (
          <button
            key={`je-${el._uid}`}
            className='group/sublist relative w-full whitespace-nowrap bg-white px-6 py-4 text-center text-sm no-underline transition-colors duration-300 hover:bg-white/90'
          >
            {title.text}
            <div className='absolute left-full top-0 hidden flex-col group-hover/sublist:flex'>
              {links.map(link => getDesktopLinks(link))}
            </div>
          </button>
        )
      }

      if (el.component === 'Link') {
        //* Is a "file" link. Probably has a PDF attached
        if (el.file) {
          return (
            <a
              href={el.file}
              target='_blank'
              key={`je-${el._uid}`}
              rel='noopener noreferrer'
              className='w-full bg-green-500 px-6 py-4 text-center text-sm text-white no-underline transition-colors duration-300 hover:bg-green-800'
            >
              {el.label}
            </a>
          )
        }

        //* Is an internal link. To be used to link between pages in the project
        if (!el.link.url || !httpRegex.test(el.link.url)) {
          return (
            <Link
              key={`je-${el._uid}`}
              to={`/${el.link.cached_url}`}
              className='block w-full truncate bg-green-500 px-6 py-4 text-center text-sm text-white no-underline transition-colors duration-300 hover:bg-green-800'
            >
              {el.label}
            </Link>
          )
        }

        //* Is an external link. To be used to link to external sites
        return (
          <a
            target='_blank'
            key={`ja-${el._uid}`}
            href={`${el.link.url}`}
            className='w-full truncate bg-green-500 px-6 py-4 text-center text-sm text-white no-underline transition-colors duration-300 hover:bg-green-800'
            rel='noreferrer'
          >
            {el.label}
          </a>
        )
      }
    },
    [header],
  )

  const getMobileLinks = useCallback(
    el => {
      if (el.component === 'Link') {
        const httpRegex = /^http/

        if (!el.link.url || !httpRegex.test(el.link.url)) {
          return (
            <Link
              key={`footer-${el._uid}`}
              to={`/${el.link.cached_url}`}
              onClick={() => setIsMobileMenuOpen(prev => !prev)}
              className='w-full px-5 py-4 text-sm text-white no-underline hover:bg-green-800'
            >
              {el.label}
            </Link>
          )
        }

        return (
          <a
            href={`${el.link.url}`}
            key={`footer-${el._uid}`}
            className='w-full px-5 py-4 text-sm text-white no-underline hover:bg-green-800'
          >
            {el.label}
          </a>
        )
      }

      if (el.component === 'Footer Links') {
        const title = el.content.find(
          item => item.component === 'Section Title',
        )
        const links = el.content.filter(item => item.component === 'Link')
        return (
          <div className='flex flex-col p-0'>
            <div className='pointer-events-none w-full bg-green-700 px-5 py-4 text-sm text-white no-underline'>
              {title.text}
            </div>
            <div className='flex flex-col pl-10'>
              {links.map(link => getMobileLinks(link))}
            </div>
          </div>
        )
      }

      if (el.component == 'Text' && user && user.role == 'admin') {
        return (
          <a
            target='_blank'
            key={`footer-${el._uid}`}
            href={`${serverURL}/users`}
            className='w-full px-5 py-4 text-sm text-white no-underline hover:bg-green-800'
            rel='noreferrer'
          >
            {el.text}
          </a>
        )
      }
    },
    [header],
  )

  return (
    <div>
      <header
        style={{ minHeight: 90 }}
        className='relative flex justify-between overflow-visible bg-white'
      >
        {/* LOGO SECTION */}
        <SbEditable content={headerStory.content}>
          <div className='flex items-center md:px-3'>
            <Link to={lng == 'es' ? '/es' : '/en'}>
              <img
                src={headerStory.content.logo || psLogo}
                alt=''
                className='h-16'
              />
            </Link>
          </div>
        </SbEditable>
        <div className='flex md:hidden'>
          <button
            onClick={() => setIsMobileMenuOpen(prev => !prev)}
            className='group border-0 border-b-4 border-solid border-transparent bg-transparent pt-2 transition-all duration-100 hover:border-green-400'
          >
            <span className='text-sm font-light'>MENU</span>
            <span
              style={{ color: '#c8c8c9', fontSize: '0.5rem' }}
              className='icon-caret-down ml-1'
            />
          </button>
        </div>

        {/* Nav Buttons Section */}
        <nav className='mr-auto hidden w-full flex-wrap border-0 border-solid border-gray-300 md:flex md:border-l'>
          {header.map(col => {
            if (
              ['Footer Links', 'Auth List Of Links'].includes(col.component)
            ) {
              if (col.component == 'Auth List Of Links' && !user) {
                return ''
              }

              return (
                <SbEditable key={`cal-${col._uid}`} content={col}>
                  <div className='customDropdownParent group relative box-border flex overflow-visible border-0 border-b-4 border-solid border-transparent px-2 pt-2 text-gray-600 hover:border-green-400'>
                    <button className='flex items-center border-none bg-transparent'>
                      <span
                        className={cls('mr-2 truncate  text-sm', {
                          'text-green-500':
                            col.component == 'Auth List Of Links',
                          'text-gray-600':
                            col.component != 'Auth List Of Links',
                        })}
                      >
                        {
                          col.content.filter(
                            el => el.component === 'Section Title',
                          )[0].text
                        }
                      </span>
                      <span
                        style={{
                          color:
                            col.component == 'Auth List Of Links'
                              ? '#4ca948'
                              : '#c8c8c9',
                          fontSize: '0.5rem',
                        }}
                        className='icon-caret-down ml-1'
                      />
                    </button>

                    <div
                      style={{
                        left: '50%',
                        top: 'calc(100% + 4px)',
                        transform: 'translate(-50%,0%)',
                      }}
                      className='customDropdownChild absolute z-10 hidden flex-col'
                    >
                      {col.content.map(el => getDesktopLinks(el))}
                    </div>
                  </div>
                </SbEditable>
              )
            }

            return ''
          })}

          <div className='ml-auto flex items-center border-0 border-solid border-gray-300 lg:border-l'>
            {!user && (
              <div className='h-full'>
                {header.map(col => {
                  if (col.component == 'Join Button') {
                    return (
                      <a
                        href={`/${col.link.cached_url}`}
                        className='flex h-full items-center px-5 text-light no-underline '
                        key={col._uid}
                      >
                        <span className='text-md text-center text-green-500'>
                          {col.label}
                        </span>
                      </a>
                    )
                  }
                })}
              </div>
            )}

            <div className='customDropdownParent relative ml-3 mr-6 flex h-full cursor-pointer items-center overflow-visible'>
              <div className='flex border-none bg-transparent'>
                <span className='mr-2 truncate text-sm text-gray-600 '>
                  {lng === 'en' ? 'English' : 'Español'}
                </span>
                <span
                  style={{ color: '#c8c8c9', fontSize: '0.5rem' }}
                  className='icon-caret-down ml-1 self-center'
                />
              </div>

              <button
                onClick={() => handleLanguageChange(lng === 'en' ? 'es' : 'en')}
                style={{ top: '100%' }}
                className='customDropdownChild absolute z-30 hidden w-full justify-center border-none bg-green-500 py-6'
              >
                <span className='truncate text-sm text-white'>
                  {lng !== 'en' ? 'English' : 'Español'}
                </span>
              </button>
            </div>
            <div className='customDropdownParent relative flex h-full cursor-pointer items-center bg-green-500 text-center text-white no-underline '></div>
          </div>
        </nav>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen ? (
        <div className='flex flex-col md:hidden bg-white'>
          <nav className='flex flex-col border-0 border-t border-solid border-gray-300'>
            {header.map((col, i) => {
              if (
                ['Footer Links', 'Auth List Of Links'].includes(col.component)
              ) {
                if (col.component == 'Auth List Of Links' && !user) {
                  return ''
                }
                return (
                  <SbEditable key={`footer-${col._uid}`} content={col}>
                    <div className='flex flex-col items-start'>
                      <button
                        className='flex w-full border-none bg-transparent p-5'
                        onClick={() =>
                          setActiveDropdownIndex(prev =>
                            prev === i ? null : i,
                          )
                        }
                      >
                        <span
                          className={cls('mr-2 self-start text-sm', {
                            'text-green-500':
                              col.component == 'Auth List Of Links',
                            'text-gray-600':
                              col.component != 'Auth List Of Links',
                          })}
                        >
                          {
                            col.content.filter(
                              el => el.component === 'Section Title',
                            )[0].text
                          }
                        </span>
                        <span
                          style={{
                            color:
                              col.component == 'Auth List Of Links'
                                ? '#4ca948'
                                : '#c8c8c9',
                            fontSize: '0.5rem',
                          }}
                          className='icon-caret-down ml-1 self-center'
                        />
                      </button>

                      {/* List of links */}
                      <div
                        className={cls(
                          'flex w-full flex-col bg-green-500 py-2',
                          { hidden: activeDropdownIndex !== i },
                        )}
                      >
                        {col.content.map(el => getMobileLinks(el))}
                      </div>
                    </div>
                  </SbEditable>
                )
              }

              return ''
            })}
          </nav>

          {/* Login & Change Menu Sections */}
          <nav className='flex flex-col border-0 border-t border-solid border-gray-300'>
            <div className='flex items-center justify-between'>
              {!user ? (
                <div className='flex h-full w-1/2 justify-center '>
                  {header.map(col => {
                    if (col.component == 'Join Button') {
                      return (
                        <a
                          key={col.link.cached_url}
                          href={`/${col.link.cached_url}`}
                          className='flex h-full w-full items-center justify-center px-5 py-5 text-light no-underline'
                        >
                          <span className='text-md text-center text-green-500'>
                            {col.label}
                          </span>
                        </a>
                      )
                    }
                  })}
                </div>
              ) : (
                <div className='customDropdownParent relative flex w-1/2 cursor-pointer justify-center overflow-visible bg-green-700 py-3 text-white'>
                  <div
                    className={cls('ml-5 h-12 w-12 rounded-full', {
                      'bg-white': !user.profileImage,
                    })}
                  >
                    <img
                      className={`h-full w-full rounded-full ${user.profileImage ? '' : 'border border-gray-200'}`}
                      src={user.profileImage || ''}
                      alt=''
                    />
                  </div>
                  <span
                    style={{ fontSize: '0.8rem' }}
                    className='icon-caret-down ml-3 mr-5 self-center'
                  />
                  <div
                    style={{ top: '100%' }}
                    className='customDropdownChild absolute z-30 hidden w-full flex-col border-none bg-green-500'
                  >
                    <button
                      onClick={loggingOut ? undefined : () => logOut()}
                      className='border-0 bg-green-500 py-3 text-white hover:bg-green-700'
                    >
                      {loggingOut ? 'Logging Out' : 'Log Out'}
                    </button>
                  </div>
                </div>
              )}

              <div className='customDropdownParent relative flex w-1/2 cursor-pointer justify-center overflow-visible py-5'>
                <div className='border-none bg-transparent '>
                  <span className='mr-2 truncate text-sm text-gray-600 '>
                    {lng === 'en' ? 'English' : 'Español'}
                  </span>
                  <span
                    style={{ color: '#c8c8c9', fontSize: '0.5rem' }}
                    className='icon-caret-down ml-1 self-center'
                  />
                </div>

                <button
                  onClick={() =>
                    handleLanguageChange(lng === 'en' ? 'es' : 'en')
                  }
                  style={{ top: '100%' }}
                  className='customDropdownChild absolute z-30 hidden w-full justify-center border-none bg-green-500 py-6'
                >
                  <span className='truncate text-sm text-white'>
                    {lng !== 'en' ? 'English' : 'Español'}
                  </span>
                </button>
              </div>
            </div>
          </nav>
        </div>
      ) : (
        ''
      )}
    </div>
  )
}



// WEBPACK FOOTER //
// /opt/build/repo/src/NavBar.jsx